import {
  LayoutDashboard,
  Plus,
  Lock,
  Library,
  CircleCheckBig,
  MessageSquare,
  Search,
  Star,
  Inbox,
  Users,
  UserCheck,
  Gift,
  Receipt,
  Calculator,
  Settings,
  LifeBuoy,
} from "lucide-react"

import { Link } from "@inertiajs/react"

import { cn } from "@/lib/utils"
import BrandLockup from "./auth/BrandLockup"
import NavAccountBlock from "./NavAccountBlock"

// Flat, task-focused nav — ports the authoritative NAV map from the design
// handoff's shared/shell.js. Glyphs mirror its ICONS map. `countKey` pulls a
// live badge count from the page's navCounts prop (only where it's cheap to
// compute). Every item carries a route: an item without one used to render as an
// inert `<div role="link">` that announced itself as a link and did nothing,
// which is the defect UPDA-74 reported. The Start-a-project CTA (above this list)
// replaces the old top-group "Post a Project" nav item, and Insider is relocated
// to the rail foot.
const NAV = [
  {
    items: [
      { key: "Dashboard", Icon: LayoutDashboard, href: "/" },
    ],
  },
  {
    group: "Manage",
    items: [
      { key: "Projects", Icon: Library, href: "/projects", countKey: "projects" },
      { key: "Approvals", Icon: CircleCheckBig, href: "/approvals", countKey: "approvals" },
      { key: "Messages", Icon: MessageSquare, href: "/messages", countKey: "messages" },
      { key: "Team", Icon: Users, href: "/team" },
    ],
  },
  {
    group: "Hire",
    items: [
      { key: "Find Freelancers", Icon: Search, href: "/find-freelancers" },
      { key: "Favorites", Icon: Star, href: "/favorites", countKey: "favorites" },
      // One screen for all three things a customer can ask a freelancer for.
      // It replaces the separate Invites / Samples / Out of Scope items, which
      // named three legacy pages Atrium never rebuilt (UPDA-74).
      { key: "Requests", Icon: Inbox, href: "/requests", countKey: "requests" },
    ],
  },
  {
    group: "Account",
    items: [
      { key: "Access", Icon: UserCheck, href: "/access" },
      { key: "Rewards", Icon: Gift, href: "/rewards" },
      { key: "Billing", Icon: Receipt, href: "/billing" },
    ],
  },
  {
    group: "Tools",
    items: [
      { key: "Pricing Calculator", Icon: Calculator, href: "/pricing-calculator" },
    ],
  },
]

function NavItem({ item, active, count }) {
  const { Icon } = item
  const isActive = item.key === active
  const isInsider = item.variant === "insider"

  const className = cn(
    "flex cursor-pointer items-center gap-[11px] rounded-[10px] px-[11px] py-[9px] text-[13.5px] font-medium transition-colors select-none",
    isActive
      ? "bg-[image:var(--nav-active-bg)] font-semibold text-foreground"
      : isInsider
        ? "bg-[image:var(--nav-insider-bg)] font-semibold text-primary"
        : "text-sidebar-foreground hover:bg-card hover:text-foreground"
  )

  const content = (
    <>
      <Icon
        strokeWidth={1.9}
        className={cn(
          "size-[18px] shrink-0",
          isActive || isInsider ? "text-primary" : "text-ink-3"
        )}
      />
      <span className="truncate">{item.label ?? item.key}</span>
      {count != null && count > 0 && (
        <span
          className={cn(
            "ml-auto rounded-full px-[7px] py-px text-[11px] font-bold",
            isActive
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-muted-foreground"
          )}
        >
          {count}
        </span>
      )}
    </>
  )

  // An action item (Support › Get help) opens something in place instead of
  // navigating, so it is a button, not a link.
  if (item.onClick) {
    return (
      <button type="button" onClick={item.onClick} className={cn(className, "w-full text-left")}>
        {content}
      </button>
    )
  }

  return (
    <Link
      href={item.href}
      aria-current={isActive ? "page" : undefined}
      className={className}
    >
      {content}
    </Link>
  )
}

// The sidebar's inner content — logo, Start-a-project CTA, grouped nav, rail
// foot — extracted so the SAME tree renders inside both the static desktop
// `<aside>` and the ≤860px off-canvas drawer (AppShell's Sheet). Nav parity
// between the two is structural, not duplicated. Expects a flex-column parent
// that owns width, height, background, and padding.
export default function SidebarContent({ active, navCounts, auth, onHelp }) {
  // Support › Get help (Help Scout Beacon: articles, chat, message) appears only where the shell has
  // live chat on (staging and production).
  const nav = onHelp
    ? [...NAV, { group: "Support", items: [{ key: "Get help", Icon: LifeBuoy, onClick: onHelp }] }]
    : NAV

  return (
    <>
      {/* The brand slot carries the `dibbly | The Urban Writers` co-brand
          lockup at the rail's width, the same asset pair the auth screens
          introduce the product with (P11-H7 Lockup); the Dibbly-only wordmark
          stays available for other uses. */}
      <div className="flex shrink-0 items-center px-2 pt-1 pb-5">
        <BrandLockup className="h-auto w-full max-w-[200px]" />
      </div>

      {/* Start-a-project CTA — the primary posting entry point, pinned under the
          logo above the scrollable nav (replaces the old "Post a Project" nav
          item). Soft-red fill + red text, not a solid-red button. */}
      <Link
        href="/post"
        className="mb-3 flex shrink-0 items-center gap-[9px] rounded-[10px] bg-[var(--red-soft)] px-[11px] py-[10px] text-[13.5px] font-semibold text-primary no-underline transition-colors hover:bg-[image:var(--nav-active-bg)]"
      >
        <Plus className="size-[18px] shrink-0" strokeWidth={2.2} />
        <span>Start a project</span>
      </Link>

      {/* Nav list scrolls internally when it outgrows the viewport; logo, CTA and
          the rail foot stay pinned. min-h-0 lets this flex child shrink below its
          content height so overflow-y engages instead of pushing the aside. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {nav.map((section, i) => (
          <div key={section.group ?? `top-${i}`}>
            {section.group && (
              <div className="px-[11px] pt-[18px] pb-[7px] text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">
                {section.group}
              </div>
            )}
            <div className="flex flex-col gap-[3px]">
              {section.items.map((item) => (
                <NavItem
                  key={item.key}
                  item={item}
                  active={active}
                  count={item.countKey ? navCounts?.[item.countKey] : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Rail foot, laid out once: Insider (the membership / price-lock hub) ·
          Settings · the signed-in customer's account block. Insider keeps its
          soft-red variant styling + lock glyph off-route, and resolves to the
          active state on /insider like Settings does. */}
      <div className="flex shrink-0 flex-col gap-[3px] pt-4">
        <NavItem item={{ key: "Insider", Icon: Lock, href: "/insider", variant: "insider" }} active={active} />
        <NavItem item={{ key: "Settings", Icon: Settings, href: "/settings" }} active={active} />
        <NavAccountBlock auth={auth} />
      </div>
    </>
  )
}
